import React, { useEffect, useState } from "react";
import {
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { signOut, updatePassword, fetchUserAttributes } from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import PersonIcon from "@mui/icons-material/Person";
import { AppButton } from "../../components/ui";
import AccountsAPI from "../../services/AccountsAPI";
import {
  formatSubscriptionDate,
  formatSubscriptionLabel,
  getRoleDisplayName,
} from "../../utility/subscriptionAccess";
import "./Account.scss";
import { getAccountPlanName } from "../../utility/billingPlans";
import { hasAccountPermission } from "../../utility/accountPermissions";

const formatInvoiceDate = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(parsedDate);
};

const formatInvoiceAmount = (amount, currency) => {
  if (amount === null || amount === undefined || !currency) {
    return null;
  }

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: String(currency).toUpperCase(),
    }).format(amount / 100);
  } catch (error) {
    return `${(amount / 100).toFixed(2)} ${String(currency).toUpperCase()}`;
  }
};

const AccountPage = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const planName = getAccountPlanName(auth);
  const canViewContent = auth.permissions?.viewWebContent === true;
  const canManageJoinCodes = auth.permissions?.manageJoinCodes === true;
  const canViewAnalytics = hasAccountPermission(auth.permissions, "viewAnalytics");
  const subscriptionLabel = formatSubscriptionLabel(
    auth.subscriptionStatus,
    auth.hasActiveSubscription
  );
  const hasStripeManagedSubscription =
    auth.subscriptionSource === "stripe" &&
    auth.subscriptionStatus === "subscribed" &&
    auth.hasActiveSubscription;
  const showBillingSection = auth.subscriptionSource === "stripe";
  const isSubscribedUser =
    auth.subscriptionStatus === "subscribed" || auth.subscriptionStatus === "perpetual";
  // User email
  const [userEmail, setUserEmail] = useState("");
  const [billingError, setBillingError] = useState("");
  const [billingSuccess, setBillingSuccess] = useState("");
  const [isUpdatingAutoRenew, setIsUpdatingAutoRenew] = useState(false);
  const [billingInvoices, setBillingInvoices] = useState([]);
  const [isBillingInvoicesLoading, setIsBillingInvoicesLoading] = useState(false);
  const [billingInvoicesError, setBillingInvoicesError] = useState("");

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  // Logout confirmation dialog
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch user email
  useEffect(() => {
    const getUserEmail = async () => {
      try {
        const attributes = await fetchUserAttributes();
        setUserEmail(attributes.email || "");
      } catch (err) {
        console.error("Error fetching user attributes:", err);
      }
    };
    getUserEmail();
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const loadBillingInvoices = async () => {
      if (!auth.userId || !showBillingSection) {
        setBillingInvoices([]);
        setBillingInvoicesError("");
        setIsBillingInvoicesLoading(false);
        return;
      }

      setIsBillingInvoicesLoading(true);
      setBillingInvoicesError("");

      try {
        const response = await AccountsAPI.get(
          `/users/${auth.userId}/billing/invoices`,
          { requiresAuth: true }
        );
        const payload = response?.data?.data ?? response?.data ?? {};

        if (!isCancelled) {
          setBillingInvoices(payload.invoices || []);
        }
      } catch (error) {
        console.error("Failed to load billing invoices", error);

        if (!isCancelled) {
          setBillingInvoices([]);
          setBillingInvoicesError(
            error?.response?.data?.message ||
              "Unable to load billing history right now."
          );
        }
      } finally {
        if (!isCancelled) {
          setIsBillingInvoicesLoading(false);
        }
      }
    };

    loadBillingInvoices();

    return () => {
      isCancelled = true;
    };
  }, [auth.userId, showBillingSection]);

  const handleLogout = async () => {
    try {
      await signOut();
      console.log("User logged out successfully.");
      navigate("/login");
    } catch (err) {
      console.error("Error during logout:", err);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError("");
    setPasswordSuccess("");

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("Please fill in all fields.");
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match.");
      return;
    }

    setChangingPassword(true);

    try {
      await updatePassword({
        oldPassword: currentPassword,
        newPassword: newPassword,
      });
      setPasswordSuccess("Password changed successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => {
        setShowPasswordForm(false);
        setPasswordSuccess("");
      }, 2000);
    } catch (err) {
      console.error("Error changing password:", err);
      if (err.name === "NotAuthorizedException") {
        setPasswordError("Current password is incorrect.");
      } else if (err.name === "InvalidPasswordException") {
        setPasswordError("New password does not meet requirements.");
      } else {
        setPasswordError(err.message || "Failed to change password.");
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const cancelPasswordChange = () => {
    setShowPasswordForm(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordError("");
    setPasswordSuccess("");
  };

  const handleAutoRenewUpdate = async (enableAutoRenew) => {
    setBillingError("");
    setBillingSuccess("");
    setIsUpdatingAutoRenew(true);

    try {
      await AccountsAPI.post(
        enableAutoRenew
          ? "/users/subscription/resume"
          : "/users/subscription/cancel",
        {},
        { requiresAuth: true }
      );
      await auth.refreshAuthSession();
      setBillingSuccess(
        enableAutoRenew
          ? "Auto-renew is back on. Your subscription will renew automatically at the end of the current term."
          : "Auto-renew has been turned off. Your plan stays active until the end of the current term."
      );
    } catch (error) {
      console.error("Failed to update subscription auto-renew", error);
      setBillingError(
        error?.response?.data?.message ||
          "Unable to update auto-renew right now. Please try again."
      );
    } finally {
      setIsUpdatingAutoRenew(false);
    }
  };

  return (
    <div className="accountPage">
      {/* Header */}
      <div className="accountHeader">
        <h1 className="accountTitle">Account</h1>
      </div>

      {hasStripeManagedSubscription &&
        !auth.subscriptionRenews &&
        auth.subscriptionEndsAt && (
          <Alert severity="warning" className="accountTopNotice">
            <strong>Auto-renew is off.</strong> Your subscription expires on{" "}
            {formatSubscriptionDate(auth.subscriptionEndsAt)} unless you turn it back on
            before then.
          </Alert>
        )}

      {/* Profile Section */}
      <div className="accountSection">
        <div className="accountProfile">
          <div className="accountAvatar">
            <PersonIcon className="avatarIcon" />
          </div>
          <div className="accountProfileInfo">
            <div className="accountName">{auth.organizationName || "User"}</div>
            <div className="accountEmail">{userEmail || "—"}</div>
          </div>
        </div>
      </div>

      <div className="accountSection">
        <div className="accountSectionTitle">License</div>
        {billingError && <Alert severity="error" sx={{ mb: 2 }}>{billingError}</Alert>}
        {billingSuccess && <Alert severity="success" sx={{ mb: 2 }}>{billingSuccess}</Alert>}
        <div className="accountPlanSection">
          <div className="accountPlanSummary">
            <div className={`accountPlanBadge accountPlanBadge--${auth.hasActiveSubscription ? "active" : "inactive"}`}>
              {subscriptionLabel}
            </div>
            <div className="accountPlanTitle">
              {planName}
            </div>
            <div className="accountPlanMeta">
              {canViewContent ? "Web content access is active." : "Web content access is inactive."}
            </div>
            <div className="accountPlanMeta">
              {canViewAnalytics ? "Educator analytics are available." : "Analytics are not included."}
              {" "}{canManageJoinCodes ? "Student join codes are available." : "Join codes are not included."}
            </div>
            {auth.permissions?.viewVrContent === true && (
              <div className="accountPlanMeta">
                {String(auth.roleName).toLowerCase() === "educatorsite"
                  ? "Unlimited web and VR access for your site."
                  : auth.educatorTrialExpiresAt && new Date(auth.educatorTrialExpiresAt) > new Date()
                  ? `Trial VR access through ${formatSubscriptionDate(auth.educatorTrialExpiresAt)}.`
                  : "VR content access is active."}
              </div>
            )}
            {auth.subscriptionBillingInterval && hasStripeManagedSubscription && (
              <div className="accountPlanMeta">
                Billed {auth.subscriptionBillingInterval === "month" ? "monthly" : "annually"}.
              </div>
            )}
            <div className="accountPlanMeta">
              Role: {getRoleDisplayName({
                roleName: auth.roleName,
                roleId: auth.roleId,
                isRoleNameLoading: auth.isAccountProfileLoading,
              })}
            </div>
            {auth.hasActiveSubscription && auth.subscriptionEndsAt && (
              <div className="accountPlanMeta">
                {hasStripeManagedSubscription && auth.subscriptionRenews
                  ? `Renews on ${formatSubscriptionDate(auth.subscriptionEndsAt)}`
                  : `Access through ${formatSubscriptionDate(auth.subscriptionEndsAt)}`}
              </div>
            )}
            {hasStripeManagedSubscription && (
              <div className="accountPlanMeta">
                Auto-renew: {auth.subscriptionRenews ? "On" : "Off"}
              </div>
            )}
          </div>
          <div className="accountPlanActions">
            {!isSubscribedUser && (
              <AppButton
                className="accountBtn"
                kind="primary"
                onClick={() => navigate("/upgrade")}
              >
                View Plans
              </AppButton>
            )}
            {hasStripeManagedSubscription && (
              <AppButton
                className="accountBtn"
                kind={auth.subscriptionRenews ? "danger" : "secondary"}
                loading={isUpdatingAutoRenew}
                loadingLabel="Updating..."
                onClick={() => handleAutoRenewUpdate(!auth.subscriptionRenews)}
              >
                {auth.subscriptionRenews
                  ? "Turn Off Auto-Renew"
                  : "Turn On Auto-Renew"}
              </AppButton>
            )}
          </div>
        </div>
      </div>

      {showBillingSection && (
        <div className="accountSection">
          <div className="accountSectionTitle">Billing history</div>
          {billingInvoicesError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {billingInvoicesError}
            </Alert>
          )}
          {isBillingInvoicesLoading ? (
            <div className="accountBillingEmptyState">Loading invoices...</div>
          ) : billingInvoices.length ? (
            <div className="accountInvoicesList">
              {billingInvoices.map((invoice) => {
                const amountLabel =
                  formatInvoiceAmount(invoice.amountPaid, invoice.currency) ||
                  formatInvoiceAmount(invoice.amountDue, invoice.currency) ||
                  "Invoice";
                const issuedDate = formatInvoiceDate(invoice.createdAt);
                const paidDate = formatInvoiceDate(invoice.paidAt);

                return (
                  <div className="accountInvoiceRow" key={invoice.id}>
                    <div className="accountInvoiceSummary">
                      <div className="accountInvoiceTitle">
                        {amountLabel}
                        {invoice.status ? ` · ${invoice.status}` : ""}
                      </div>
                      <div className="accountInvoiceMeta">
                        {issuedDate ? `Issued ${issuedDate}` : "Stripe invoice"}
                        {paidDate ? ` · Paid ${paidDate}` : ""}
                        {invoice.number ? ` · ${invoice.number}` : ""}
                      </div>
                    </div>
                    <div className="accountInvoiceActions">
                      {invoice.hostedInvoiceUrl && (
                        <AppButton
                          className="accountBtn"
                          kind="secondary"
                          component="a"
                          href={invoice.hostedInvoiceUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View invoice
                        </AppButton>
                      )}
                      {invoice.invoicePdfUrl && (
                        <AppButton
                          className="accountBtn"
                          kind="ghost"
                          component="a"
                          href={invoice.invoicePdfUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          PDF
                        </AppButton>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="accountBillingEmptyState">
              Invoices will appear here after a payment is charged.
            </div>
          )}
        </div>
      )}

      {/* Security Section */}
      <div className="accountSection">
        <div className="accountSectionTitle">Security</div>
        <div className="accountSettingsList">
          <div className="accountSettingsItem">
            <div className="accountSettingsInfo">
              <div className="accountSettingsLabel">Password</div>
              <div className="accountSettingsDesc">
                {showPasswordForm ? "Enter your current and new password" : "Change your account password"}
              </div>
            </div>
            {!showPasswordForm && (
              <AppButton
                className="accountBtn"
                kind="secondary"
                onClick={() => setShowPasswordForm(true)}
              >
                Change Password
              </AppButton>
            )}
          </div>

          {showPasswordForm && (
            <div className="passwordForm">
              <TextField
                label="Current Password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                size="small"
                helperText="Must be at least 8 characters"
              />
              <TextField
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                size="small"
              />

              {passwordError && (
                <div className="accountMessage error">{passwordError}</div>
              )}
              {passwordSuccess && (
                <div className="accountMessage success">{passwordSuccess}</div>
              )}

              <div className="passwordFormActions">
                <AppButton
                  className="accountBtn"
                  kind="primary"
                  onClick={handlePasswordChange}
                  disabled={changingPassword}
                >
                  {changingPassword ? "Saving..." : "Save Password"}
                </AppButton>
                <AppButton
                  className="accountBtn"
                  kind="ghost"
                  onClick={cancelPasswordChange}
                  disabled={changingPassword}
                >
                  Cancel
                </AppButton>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sign Out Section */}
      <div className="accountSection accountDangerZone">
        <div className="accountLogoutSection">
          <div className="accountLogoutInfo">
            <div className="accountLogoutTitle">Sign out</div>
            <div className="accountLogoutDesc">
              You will need to sign in again to access your dashboard
            </div>
          </div>
          <AppButton
            className="accountBtnDanger"
            kind="danger"
            onClick={() => setLogoutDialogOpen(true)}
          >
            Sign Out
          </AppButton>
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        PaperProps={{ className: "accountDialogPaper" }}
      >
        <DialogTitle>Sign Out</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to sign out of your account?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <AppButton className="accountBtn" kind="ghost" onClick={() => setLogoutDialogOpen(false)}>
            Cancel
          </AppButton>
          <AppButton className="accountBtnDanger" kind="danger" onClick={handleLogout}>
            Sign Out
          </AppButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AccountPage;
